// features/reports/components/PeriodoSelector.tsx
// ============================================================================
// Mes | Trimestre | Semestre (Mabel 23-oct-2026: «¿los reportes se pueden generar por mes, trimestre y semestre?»).
// Periodos de CALENDARIO (ene–mar, abr–jun, jul–sep, oct–dic; ene–jun y jul–dic), los mismos que lib/letsmovePeriodo.js.
// El periodo se identifica por cualquier mes que contenga (`mes`); « » saltan de periodo en periodo.
// Ventanas MÓVILES (Mabel 24-oct-2026: «¿puedo recorrer los trimestres, por ejemplo feb–abril?»): ‹ › mueven el periodo
// un mes; entonces `movil` = true y `mes` es el 1.er mes de la ventana (feb–abr, mar–ago…). «Calendario» regresa al de siempre.
// ============================================================================
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Loader2 } from 'lucide-react';
import { Skeleton } from '../../../components/Skeleton';
import type { TipoPeriodo } from '../api';

export type VistaPeriodo = 'mes' | TipoPeriodo;
const MES3 = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const ORD = ['1er', '2º', '3er', '4º'];
const N: Record<TipoPeriodo, number> = { trimestre: 3, semestre: 6 };

export const mesLocal = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Tijuana' }).slice(0, 7);
export function sumaMeses(ym: string, n: number) { const [y, m] = ym.split('-').map(Number); const d = new Date(y, m - 1 + n, 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; }

/** Periodo de calendario que contiene `ym`; con `movil`, la ventana que empieza en `ym` (mismo cálculo que lib/letsmovePeriodo.js). */
export function periodoDe(tipo: TipoPeriodo, ym: string, movil = false) {
  const [y, m] = ym.split('-').map(Number);
  const n = N[tipo], idx = Math.floor((m - 1) / n), m0 = idx * n + 1;
  const alineado = !movil || (m - 1) % n === 0;
  const desde = alineado ? `${y}-${String(m0).padStart(2, '0')}` : ym;
  const hasta = sumaMeses(desde, n - 1);
  const mes3 = (mm: string) => MES3[Number(mm.slice(5)) - 1];
  const tramo = `${mes3(desde)}–${mes3(hasta)}`;
  const nombre = tipo === 'trimestre' ? 'Trimestre' : 'Semestre';
  const titulo = alineado
    ? (tipo === 'trimestre' ? `${ORD[idx]} trimestre ${y}` : `${idx === 0 ? '1er' : '2º'} semestre ${y}`)
    : desde.slice(0, 4) === hasta.slice(0, 4) ? `${nombre} ${tramo} ${y}` : `${nombre} ${mes3(desde)} ${desde.slice(0, 4)}–${mes3(hasta)} ${hasta.slice(0, 4)}`;
  return { desde, hasta, titulo, tramo, n, movil: !alineado };
}

const navBtn = 'rounded-lg border border-line bg-surface p-2 text-content-2 transition hover:bg-surface-2 disabled:opacity-40';

/** Botones Mes | Trimestre | Semestre. */
export function SelectorVista({ vista, onChange }: { vista: VistaPeriodo; onChange: (v: VistaPeriodo) => void }) {
  return (
    <div className="inline-flex rounded-lg border border-line bg-surface p-0.5 text-sm" role="tablist" aria-label="Periodo" data-testid="selector-periodo">
      {([['mes', 'Mes'], ['trimestre', 'Trimestre'], ['semestre', 'Semestre']] as const).map(([k, label]) => (
        <button key={k} role="tab" aria-selected={vista === k} onClick={() => onChange(k)}
          className={`whitespace-nowrap rounded-md px-3 py-1.5 transition ${vista === k ? 'bg-accent text-on-accent shadow-sm' : 'text-content-2 hover:text-content'}`}>{label}</button>
      ))}
    </div>
  );
}

/** « » saltan un periodo entero, ‹ › lo mueven un mes (ventana móvil). `maxAdelante` = cuántos periodos se puede avanzar
 *  desde hoy (0 = la ventana tiene que haber empezado ya). */
export function NavPeriodo({ tipo, mes, movil = false, onChange, maxAdelante = 1 }:
  { tipo: TipoPeriodo; mes: string; movil?: boolean; onChange: (ym: string, movil: boolean) => void; maxAdelante?: number }) {
  const p = periodoDe(tipo, mes, movil), hoy = periodoDe(tipo, mesLocal());
  const tope = maxAdelante === 0 ? mesLocal() : sumaMeses(hoy.desde, N[tipo] * maxAdelante);
  const nombre = tipo === 'trimestre' ? 'trimestre' : 'semestre';
  const ir = (ym: string, mov: boolean) => onChange(ym, mov && periodoDe(tipo, ym, true).movil);
  return (
    <div className="flex max-w-full flex-wrap items-center gap-1.5" data-testid="nav-periodo">
      <button onClick={() => ir(sumaMeses(p.desde, -p.n), p.movil)} title={`${nombre} anterior completo`} aria-label={`${nombre} anterior`} className={navBtn}><ChevronsLeft size={16} /></button>
      <button onClick={() => ir(sumaMeses(p.desde, -1), true)} title="Mover el periodo un mes atrás" aria-label="Un mes atrás" className={navBtn} data-testid="periodo-mes-atras"><ChevronLeft size={16} /></button>
      <span className="min-w-[9.5rem] rounded-lg border border-line bg-surface px-3 py-1.5 text-center text-sm text-content" data-testid="periodo-titulo">
        {p.titulo}{!p.movil && <> <span className="whitespace-nowrap text-content-3">({p.tramo})</span></>}
      </span>
      <button onClick={() => ir(sumaMeses(p.desde, 1), true)} disabled={sumaMeses(p.desde, 1) > tope} title="Mover el periodo un mes adelante" aria-label="Un mes adelante" className={navBtn} data-testid="periodo-mes-adelante"><ChevronRight size={16} /></button>
      <button onClick={() => ir(sumaMeses(p.desde, p.n), p.movil)} disabled={sumaMeses(p.desde, p.n) > tope} title={`${nombre} siguiente completo`} aria-label={`${nombre} siguiente`} className={navBtn}><ChevronsRight size={16} /></button>
      {(p.movil || p.desde !== hoy.desde) && (
        <button onClick={() => onChange(mesLocal(), false)} className={`${navBtn} text-xs`} title={`Regresar al ${nombre} de calendario de hoy`}>{p.movil ? 'Calendario' : 'Hoy'}</button>
      )}
    </div>
  );
}

/** Mientras se suma el periodo: aviso claro + esqueleto con la forma del reporte (tarjetas y tabla), sin saltos. */
export function CargandoPeriodo({ tipo, mes, movil = false }: { tipo: TipoPeriodo; mes: string; movil?: boolean }) {
  const p = periodoDe(tipo, mes, movil);
  return (
    <div className="space-y-4" data-testid="cargando-periodo" aria-busy="true">
      <p className="flex items-center gap-2 rounded-xl border border-line bg-surface px-4 py-3 text-sm text-content-2">
        <Loader2 size={16} className="shrink-0 animate-spin text-accent" />
        <span>Calculando el {tipo} {p.tramo}: sumamos mes por mes…</span>
      </p>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-20 w-full rounded-xl" />)}
      </div>
      <Skeleton className="h-64 w-full rounded-2xl" />
    </div>
  );
}

/** Pastilla discreta cuando ya hay datos en pantalla y se está recalculando. */
export function Actualizando() {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface px-2.5 py-1 text-xs text-content-3" data-testid="actualizando">
      <Loader2 size={12} className="animate-spin" /> Actualizando…
    </span>
  );
}
